/**
 * Multi-Kurs-Datenschicht.
 *
 * Verwaltet eine LISTE von Kursen (nicht einen fest verdrahteten). v1 zeigt nur
 * den Default-Kurs aus der Config, aber Navigation und Caching sind auf N Kurse
 * ausgelegt. Lädt Manifest + Kapitel über den geschützten API-Client und
 * übersetzt via Adapter in das generische Block-Modell.
 */
import { signal } from "@preact/signals";
import { endpoints } from "../api/endpoints";
import { config } from "../config";
import { chapterToBlocks, manifestToCourse } from "./adapter-v2";
import type { Chapter, Course } from "./types";

/** Aktiver Kurs (v1: Default aus Config). */
export const activeCourseId = signal<string>(config.defaultCourseId);

const courseCache = new Map<string, Course>();
const chapterCache = new Map<string, Chapter>();

function chapterKey(courseId: string, moduleId: string, blockId: string): string {
  return `${courseId}/${moduleId}/${blockId}`;
}

export async function loadCourse(courseId = activeCourseId.value): Promise<Course> {
  const cached = courseCache.get(courseId);
  if (cached) return cached;
  const res = await endpoints.manifest(courseId);
  const course = manifestToCourse(res.course);
  courseCache.set(courseId, course);
  return course;
}

export async function loadChapter(
  moduleId: string,
  blockId: string,
  courseId = activeCourseId.value,
): Promise<Chapter> {
  const key = chapterKey(courseId, moduleId, blockId);
  const cached = chapterCache.get(key);
  if (cached) return cached;
  const res = await endpoints.chapter(courseId, moduleId, blockId);
  const chapter = chapterToBlocks(res.chapter, moduleId);
  chapterCache.set(key, chapter);
  return chapter;
}

/** Cache leeren (z. B. nach Logout oder Kurs-Update). */
export function resetCourseCache(): void {
  courseCache.clear();
  chapterCache.clear();
}
